import {
  DOME_SUBTYPE,
  EAR_MOLD_SUBTYPE,
  RECEIVER_SUBTYPE,
  TUBE_SUBTYPE,
} from "constants/products";
import { HEARING_AID_PRODUCT_TYPE } from "constants/sales";

export type ReceiverLength = number | string | null;

/**
 * The value to SEND for a receiver's length. Staff often know the gain but not the length,
 * so a blank field must stay blank (`null`), never become `0` (`Number("")` is 0, and 0 is a
 * real receiver size) or `""` (the backend's length type rejects an empty string, which
 * fails the whole save). A numeric entry is sent as a number. Anything else (the backend
 * also stores letter sizes "XS"…"XL") is passed through trimmed, so editing a record that
 * holds one keeps it instead of turning it into `NaN` (sent as `null`).
 */
export const toReceiverLength = (value: unknown): ReceiverLength => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = String(value).trim();
  if (text === "") return null;
  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : text;
};

/** True when a stored receiver length is a real value, including 0. */
export const hasReceiverLength = (value: unknown): boolean =>
  toReceiverLength(value) !== null;

export const getSpecificationData = (
  specification: any,
  type: number,
  subtype: number | null
) => {
  if (!specification) return {};
  const specificationObject =
    typeof specification === "string"
      ? JSON.parse(specification.replaceAll("'", '"'))
      : specification;
  if (type.toString() === HEARING_AID_PRODUCT_TYPE.toString()) {
    return {
      color: specificationObject?.color || "",
      battery: specificationObject?.battery || "",
    };
  } else {
    switch (subtype?.toString()) {
      case RECEIVER_SUBTYPE.toString():
        return {
          gain: specificationObject?.gain || "",
          length: toReceiverLength(specificationObject?.length),
        };
      case EAR_MOLD_SUBTYPE.toString():
        return {
          canal_length: specificationObject?.canal_length || "",
          venting: specificationObject?.venting || "",
          material: specificationObject?.material || "",
          options: specificationObject?.options || "",
        };
      case DOME_SUBTYPE.toString():
        return {
          type: specificationObject?.type || "",
          size: specificationObject?.size || "",
        };
      case TUBE_SUBTYPE.toString():
        return {
          size: specificationObject?.size || "",
        };
      default:
        return {};
    }
  }
};
